import * as React from 'react';
import { IConditionErrors } from '../../../../../interface/conditions.interface';
import {
    ConditionType,
    PreCondition,
    PostCondition,
    CONDITION_TYPES,
    QuestionSummary,
    QuestionID
} from '@nexxt/common/types';
import { defaultRules } from '../default';
import PrePostCondition from './PrePostCondition';
import { getConfirmationBox } from '../../../../common/swal/Sweetalert';
import { Question } from '@nexxt/common/types';
import { alertMessage } from '../../../../../constants/alertMessage';
import { IToastNotification } from '../../../../../HOC/Notifications';
import { INCA_VARIABLE_FUNCTION_TYPES } from '@nexxt/common/incaLogic/types';

export interface ConditionsProps {
    toastNotification: IToastNotification;
    preconditions: PreCondition[];
    postconditions: PostCondition[];
    editIndex: number;
    editType: ConditionType;
    editRuleIndex: number;
    filteredQuestionSummaries: QuestionSummary[];
    flattenQuestionSummaries: QuestionSummary[];
    questions: { [key: number]: QuestionSummary };
    preConditionErrorObject: IConditionErrors;
    postConditionErrorObject: IConditionErrors;
    healthCondition: any;
    currentQuestionId: QuestionID;
    onSaveEditCondition: (type: ConditionType) => void;
    updateCondition: (
        type: ConditionType,
        conditions: (PreCondition & Partial<PostCondition>)[],
        options?: any
    ) => void;
    getQuestionById: (questionId: number, options) => Promise<any>;
    editCondition: (type, index) => void;
    cancelCondition: () => void;
    editRule: (ruleIndex: number) => void;
    deleteCondition: (type: ConditionType, conditionId) => void;
    onCancelAddRule: () => void;
    editEnabled: boolean;
}

class Conditions extends React.Component<ConditionsProps, unknown> {
    /**
     * Returns a copy of @condition
     * @TODO: why not just use _.cloneDeep ?
     */
    copyCondition = (condition: PreCondition & Partial<PostCondition>) => {
        const newCondition = { ...condition };
        newCondition.rules = newCondition.rules.map((r) => ({ ...r }));
        if (newCondition.destinationQ) {
            newCondition.destinationQ = { ...newCondition.destinationQ };
        }
        return condition;
    };

    /**
     * Returns list of conditions in current editor of @type
     */
    getConditions = (type: ConditionType) => {
        let currentCondition: PreCondition & Partial<PostCondition>,
            conditions: (PreCondition & Partial<PostCondition>)[];
        const editIndex = this.props.editIndex;
        if (type === CONDITION_TYPES.PRECONDITION) {
            conditions = [...this.props.preconditions];
            currentCondition = this.copyCondition(conditions[editIndex]);
        } else {
            conditions = [...this.props.postconditions];
            currentCondition = this.copyCondition(conditions[editIndex]);
        }
        return {
            editIndex,
            currentCondition,
            conditions
        };
    };

    /**
     * Returns an updater function generator which, depending on the specified condition @type
     * and subsequently a @ruleIndex to specify the rule, updates the reference question to that
     * specified by @value
     * @TODO: refactor to avoid nested function generation + add typing
     */
    updateRuleReferenceQuestion =
        (type: ConditionType) => (ruleIndex: number) => (value) => {
            const { currentCondition, conditions, editIndex } =
                this.getConditions(type);
            let currentRule;
            if (currentCondition) {
                currentRule = { ...currentCondition.rules[ruleIndex] };
                currentRule.selectedReferenceQ = {
                    id: value.id,
                    label: value.label || value.title,
                    value: value.value || value.id
                };
                currentRule.compareOptions = null;
                currentCondition.rules.splice(ruleIndex, 1, currentRule);
            }

            conditions.splice(editIndex, 1, currentCondition);
            this.props.updateCondition(type, conditions);
        };

    /**
     * Returns an updater function generator which, depending on the specified condition @type
     * and subsequently a @ruleIndex to specify the rule, updates the rule operator to that
     * specified by @value
     * @TODO: refactor to avoid nested function generation + add typing
     */
    updateRuleOperator =
        (type: ConditionType) => (ruleIndex: number) => (value) => {
            const { currentCondition, conditions, editIndex } =
                this.getConditions(type);
            if (currentCondition) {
                const currentRule = { ...currentCondition.rules[ruleIndex] };
                currentRule.selectedOperator = value;
                if (
                    [
                        INCA_VARIABLE_FUNCTION_TYPES.IS_DISPLAYED,
                        INCA_VARIABLE_FUNCTION_TYPES.IS_NOT_DISPLAYED
                    ].includes(value?.type)
                ) {
                    currentRule.compareType = CONDITION_TYPES.CUSTOM_VALUE;
                }
                currentCondition.rules.splice(ruleIndex, 1, currentRule);
            }
            conditions.splice(editIndex, 1, currentCondition);
            this.props.updateCondition(type, conditions);
        };

    /**
     * Returns an updater function generator which, depending on the specified condition @type
     * and subsequently a @ruleIndex to specify the rule, updates the rule comparison value to that
     * specified by @value and corresponding to the specified @optionType
     * @TODO: refactor to avoid nested function generation + add typing
     */
    updateRuleValue =
        (type: ConditionType) =>
        (index: number) =>
        (
            optionType: 'options' | 'number' | 'string' | 'boolean',
            value,
            compareType?: CONDITION_TYPES
        ) => {
            const { currentCondition, conditions, editIndex } =
                this.getConditions(type);
            if (currentCondition) {
                const currentRule = { ...currentCondition.rules[index] };
                switch (optionType) {
                    case 'options':
                        currentRule.compareOptions = value;
                        currentRule.compareTargetIds = value;
                        currentRule.compareType = compareType;
                        break;
                    case 'number':
                        currentRule.compareNumber = value;
                        currentRule.compareType = compareType;
                        break;
                    case 'string':
                        currentRule.compareCustomValue = value;
                        currentRule.compareType = compareType;
                        break;
                    case 'boolean':
                        currentRule.compareType = compareType;
                        break;
                }

                currentCondition.rules.splice(index, 1, currentRule);
            }
            conditions.splice(editIndex, 1, currentCondition);
            this.props.updateCondition(type, conditions);
        };

    /**
     * Saves the condition being edited, unless any rules are currently
     * open in which case an error notification is sent to user.
     */
    onSaveEditCondition = (type: ConditionType) => () => {
        // prevent saving when any of rules are still opened
        if (this.props.editRuleIndex >= 0) {
            this.props.toastNotification.error(
                'Please finish editing condition rules'
            );
            return;
        }
        this.props.onSaveEditCondition(type);
    };

    /**
     * Deletes rule corresponding to @index
     * Returns a function based on condition @type
     */
    deleteRule = (type: ConditionType) => (index: number) => async () => {
        const result = await getConfirmationBox({
            message: alertMessage.CONFIRM_DELETE_ITEM,
            showCloseButton: true,
            confirmButtonColor: '',
            title: alertMessage.DELETE_ITEM
        });
        if (result.value) {
            const { currentCondition, conditions, editIndex } =
                this.getConditions(type);
            if (currentCondition) {
                const currentRule = currentCondition.rules[index];
                if (currentRule.ruleID > 0) {
                    if (currentCondition.deletedRuleIDs) {
                        currentCondition.deletedRuleIDs.push(
                            currentRule.ruleID
                        );
                    } else {
                        currentCondition.deletedRuleIDs = [currentRule.ruleID];
                    }
                }
                currentCondition.rules.splice(index, 1);
            }
            conditions.splice(editIndex, 1, currentCondition);
            this.props.updateCondition(type, conditions);
        }
    };

    deleteCondition = (type: ConditionType, index: number) => () => {
        this.props.deleteCondition(type, index);
    };

    /**
     * Returns function which updates condition's redirect question specified by @value
     */
    updateRedirectQuestion = (type: ConditionType) => (value) => {
        const { currentCondition, conditions, editIndex } =
            this.getConditions(type);
        if (currentCondition) {
            currentCondition.destinationQ = {
                label: value.title || value.label,
                value: value.id
            };
        }
        conditions.splice(editIndex, 1, currentCondition);
        this.props.updateCondition(type, conditions);
    };

    onSaveRule = (index, type) => () => {
        if (type === CONDITION_TYPES.PRECONDITION) {
            this.props.updateCondition(type, this.props.preconditions, {
                editRuleIndex: -1
            });
        } else {
            this.props.updateCondition(type, this.props.postconditions, {
                editRuleIndex: -1
            });
        }
    };

    onEdit = (type, index) => () => {
        this.props.editCondition(type, index);
    };

    onEditRule = (index) => () => {
        this.props.editRule(index);
    };

    onAddRule = (type: ConditionType) => () => {
        const { currentCondition, conditions } = this.getConditions(type);
        if (currentCondition) {
            currentCondition.rules.push(defaultRules());
        }
        this.props.updateCondition(type, conditions, {
            editRuleIndex: currentCondition.rules.length - 1
        });
    };

    render() {
        return (
            <>
                {this.props.preconditions.map((condition, index) => {
                    const conditionType = CONDITION_TYPES.PRECONDITION;
                    return (
                        <PrePostCondition
                            key={condition.conditionID}
                            prepost={true}
                            index={index}
                            condition={condition}
                            errorObject={
                                this.props.preConditionErrorObject[index]
                            }
                            onAnswerTypeSelect={this.updateRuleOperator(
                                conditionType
                            )}
                            onCancel={this.props.cancelCondition}
                            onAddRule={this.onAddRule(conditionType)}
                            onEdit={this.onEdit(conditionType, index)}
                            onDelete={this.deleteCondition(
                                conditionType,
                                index
                            )}
                            onEditRule={this.onEditRule}
                            onSaveRule={this.onSaveRule}
                            onDeleteRule={this.deleteRule(conditionType)}
                            onDone={this.onSaveEditCondition(conditionType)}
                            onQuestionSelect={this.updateRuleReferenceQuestion(
                                conditionType
                            )}
                            onValueChange={this.updateRuleValue(conditionType)}
                            filteredQuestionSummaries={
                                this.props.filteredQuestionSummaries
                            }
                            flattenQuestionSummaries={
                                this.props.flattenQuestionSummaries
                            }
                            questions={this.props.questions}
                            editIndex={
                                this.props.editType === conditionType
                                    ? this.props.editIndex
                                    : -1
                            }
                            ruleEditIndex={
                                this.props.editType === conditionType
                                    ? this.props.editRuleIndex
                                    : -1
                            }
                            onCancelAddRule={this.props.onCancelAddRule}
                            editEnabled={this.props.editEnabled}
                            healthCondition={this.props.healthCondition}
                            currentQuestionId={this.props.currentQuestionId}
                        />
                    );
                })}

                {this.props.postconditions.map((condition, index) => {
                    const conditionType = CONDITION_TYPES.POSTCONDITION;

                    return (
                        <PrePostCondition
                            key={condition.conditionID}
                            prepost={false}
                            index={index}
                            condition={condition}
                            errorObject={
                                this.props.postConditionErrorObject[index]
                            }
                            onAnswerTypeSelect={this.updateRuleOperator(
                                conditionType
                            )}
                            onCancel={this.props.cancelCondition}
                            onEdit={this.onEdit(conditionType, index)}
                            onDelete={this.deleteCondition(
                                conditionType,
                                index
                            )}
                            onAddRule={this.onAddRule(conditionType)}
                            onEditRule={this.onEditRule}
                            onSaveRule={this.onSaveRule}
                            onDeleteRule={this.deleteRule(conditionType)}
                            onDone={this.onSaveEditCondition(conditionType)}
                            onQuestionSelect={this.updateRuleReferenceQuestion(
                                conditionType
                            )}
                            onValueChange={this.updateRuleValue(conditionType)}
                            onDestinationQuestionSelect={this.updateRedirectQuestion(
                                conditionType
                            )}
                            filteredQuestionSummaries={
                                this.props.filteredQuestionSummaries
                            }
                            flattenQuestionSummaries={
                                this.props.flattenQuestionSummaries
                            }
                            questions={this.props.questions}
                            editIndex={
                                this.props.editType === conditionType
                                    ? this.props.editIndex
                                    : -1
                            }
                            ruleEditIndex={
                                this.props.editType === conditionType
                                    ? this.props.editRuleIndex
                                    : -1
                            }
                            onCancelAddRule={this.props.onCancelAddRule}
                            editEnabled={this.props.editEnabled}
                            healthCondition={this.props.healthCondition}
                            currentQuestionId={this.props.currentQuestionId}
                        />
                    );
                })}
            </>
        );
    }
}

export default Conditions;
